import { AxiosError } from 'axios';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { AutocompleteOption } from '@dxb-web-ui-framework/design-system';
import { APIGatewayResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { KeyedMutator } from 'swr/dist';
import { FavouriteTransfer } from '../../domain/FavouriteTransfer/entities';
import { Currency } from '../../domain/Currency/entities';
import { DeleteFavouriteTransferResponseBody } from '../../domain/FavouriteTransfer/entities/DeleteFavouriteTransferResponseDTO';
import { EditFavouriteTransferResponseBody } from '../../domain/FavouriteTransfer/entities/EditFavouriteTransferResponseDTO';
import { FavouriteTransfersList } from '../../domain/FavouriteTransfer/entities/FavouriteTransfersListResponseDTO';

export type FavouriteTransfersListFilters = {
  pageSize: number;
  pageOffset: number;
  name?: string;
  debitAccountNumber?: AutocompleteOption;
  creditAccountNumber?: string;
  amountFrom?: string;
  amountTo?: string;
  currency?: AutocompleteOption;
};

export enum FavouriteTransfersActionEnum {
  edit = 'EDIT',
  delete = 'DELETE',
  share = 'SHARE',
  useTemplate = 'USE_TEMPLATE',
}

export interface FavouriteTransfersState {
  action: Nullable<FavouriteTransfersActionEnum>;
  favouriteTransfersListData?: Nullable<FavouriteTransfersList>;
  favouriteTransfersRefetch?: KeyedMutator<FavouriteTransfersList> | undefined;
  currencies: Currency[] | [];
  selectedFavouriteTransfer: Nullable<FavouriteTransfer>;
  favouriteTransferToEdit: Nullable<FavouriteTransfer & { timestamp: string }>;
  favouriteTransferToDelete: Nullable<FavouriteTransfer & { timestamp: string }>;
  editFavouriteTransferData: Nullable<EditFavouriteTransferResponseBody>;
  deleteFavouriteTransferData: Nullable<DeleteFavouriteTransferResponseBody>;
  shouldShare: Nullable<boolean>;
  loadingState: {
    isLoadingFavouriteTransfersList: boolean;
    isLoadingEditFavouriteTransfer: boolean;
    isLoadingDeleteFavouriteTransfer: boolean;
    isLoadingCurrencies: boolean;
  };
  errorState: {
    favouriteTransfersDataError?: AxiosError<
      APIGatewayResponse<unknown>,
      { code: string; type: string; param: string; message: string }
    >;
    editFavouriteTransferError?: AxiosError<
      APIGatewayResponse<unknown>,
      { code: string; type: string; param: string; message: string }
    >;
    deleteFavouriteTransferError?: AxiosError<
      APIGatewayResponse<unknown>,
      { code: string; type: string; param: string; message: string }
    >;
    currenciesError?: AxiosError<APIGatewayResponse<Currency[]>, any>;
  };
  onFavouriteTransferAction: (
    selectedFavouriteAction?: FavouriteTransfer,
    action?: FavouriteTransfersActionEnum,
  ) => void;
  onEditFavouriteTransfer: (alias?: string) => void;
  onDeleteFavouriteTransfer: () => void;
  onShareFavouriteTransfer: () => void;
  onUseTemplateFavouriteTransfer: (callback?: Function) => void;
  favouriteTransfersListFilters: Partial<FavouriteTransfersListFilters>;
  onFavouriteTransferListFiltersChange: (
    filters: Partial<FavouriteTransfersListFilters>,
    toToggleFilterDrawer?: boolean,
    nextPage?: number,
    totalPages?: number,
  ) => void;
  onResetFavouriteTransferListFilters: () => void;
  onResetSelectedFavouriteTransfer: () => void;
  // favourite transfer filter drawer
  isFavouriteTransfersFilterDrawerOpen: boolean;
  toggleFavouriteTransfersFilterDrawer: () => void;
  setFavouriteTransfersFilterDrawerClose: () => void;
  meta: {
    timestamp: string;
  };
}

export enum FavouriteTransfersActionTypes {
  GetFavouriteTransfers = '@@FavouriteTransfers/GET_FAVOURITE_TRANSFERS',
  SetSelectedFavouriteTransfer = '@@FavouriteTransfers/SET_SELECTED_FAVOURITE_TRANSFER',
  SetFavouriteTransferToEdit = '@@FavouriteTransfers/SET_FAVOURITE_TRANSFER_TO_EDIT',
  SetFavouriteTransferToDelete = '@@FavouriteTransfers/SET_FAVOURITE_TRANSFER_TO_DELETE',
  SetFavouriteTransfersListFilter = '@@FavouriteTransfers/SET_FAVOURITE_TRANSFERS_LIST_FILTERS',
  SetIsFavouriteTransfersFitlerDrawerOpen = '@@FavouriteTransfers/SET_FAVOURITE_TRANSFER_FILTER_DRAWER_OPEN',
  SetAction = '@@FavouriteTransfers/SET_ACTION',
}
